import { createSelector } from '@ngrx/store';
import { firmCollaboratorFeature } from './firm-collaborators.reducer';
import { TranslatableStringType } from '../../i18n/types';

type ErrorCodeTextMapType = {
  PAYING_WITHOUT_EDIT_RIGHTS: string;
  MISSING_FIRM_CUSTOMER_NUMBER: string;
  FIRM_COLLABORATOR: string;
  FIRM_COLLABORATOR_WITHOUT_COLLABORATORS: string;
  GENERAL_CUSTOMER_NUMBER_RULE_ERROR: string;
};

export type CollaboratorTextTranslations = {
  loading: TranslatableStringType;
  unknownErrorOccurred: TranslatableStringType;
  noCollaboratorsFound: TranslatableStringType;
  onlyCoworkersOfAFirm: TranslatableStringType;
  cloudDatabaseKeyMustExists: TranslatableStringType;
} & ErrorCodeTextMapType;

const { selectFirmCollaborators } = firmCollaboratorFeature;

const selectFirmCollaboratorStateByClientId = (clientId: number) =>
  createSelector(selectFirmCollaborators, (state) => state[clientId] || null);

const selectFirmCollaboratorsByClientId = (clientId: number) =>
  createSelector(selectFirmCollaboratorStateByClientId(clientId), (state) => state?.collaborators || null);

const selectFirmCollaboratorNamesByClientId = (clientId: number) =>
  createSelector(selectFirmCollaboratorsByClientId(clientId), (collaborators) => {
    if (!collaborators) {
      return null;
    }
    return collaborators.map((firm) => firm.name);
  });

const selectMetaUserIdByClientId = (clientId: number) =>
  createSelector(selectFirmCollaboratorStateByClientId(clientId), (state) => state?.userId || null);

const canEditCollaboratorsForClientId = (clientId: number) =>
  createSelector(selectFirmCollaboratorStateByClientId(clientId), (state) => {
    if (!state) {
      return false;
    }
    return !state?.validationError;
  });

const existsFirmCollaboratorsForClientInState = (clientId: number) =>
  createSelector(selectFirmCollaboratorStateByClientId(clientId), (state) => Boolean(state));

const selectCollaboratorsText = (clientId: number, collaboratiorsTextTranslations: CollaboratorTextTranslations) =>
  // eslint-disable-next-line complexity
  createSelector(selectFirmCollaboratorStateByClientId(clientId), (state) => {
    if (!state || state.collaborators === null) {
      return collaboratiorsTextTranslations.loading;
    }

    if (state.validationError) {
      return (
        collaboratiorsTextTranslations[state.validationError] ?? collaboratiorsTextTranslations.unknownErrorOccurred
      );
    }

    if (state.collaborators.length === 0) {
      return collaboratiorsTextTranslations.noCollaboratorsFound;
    }

    return state.collaborators.map((collaborator) => collaborator.name).join(', ');
  });

export const FirmCollaboratorSelectors = {
  selectFirmCollaboratorsByClientId,
  selectFirmCollaboratorNamesByClientId,
  selectMetaUserIdByClientId,
  selectFirmCollaboratorStateByClientId,
  existsFirmCollaboratorsForClientInState,
  canEditCollaboratorsForClientId,
  selectCollaboratorsText,
};
